import { useEffect, useRef, useState } from 'react';

export type ChatSocketState = 'connecting' | 'open' | 'closed';
export type ChatRealtimeEvent =
  | { event: 'chat.message.created'; chat_id: number; message: unknown }
  | { event: 'chat.read.updated'; chat_id: number; user_id: number; last_read_message_id: number; read_at: string };

const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000];

function websocketUrl(path: string) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}`;
}

export function useReconnectingWebSocket<T>(
  path: string | null,
  onEvent: (event: T) => void,
  onOpen?: () => void,
) {
  const onEventRef = useRef(onEvent);
  const onOpenRef = useRef(onOpen);
  const [state, setState] = useState<ChatSocketState>('closed');
  onEventRef.current = onEvent;
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (!path) {
      setState('closed');
      return;
    }
    let active = true;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let reconnectAttempt = 0;

    const clearReconnectTimer = () => {
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    };

    const scheduleReconnect = () => {
      if (!active || !navigator.onLine) return;
      clearReconnectTimer();
      const delay = RECONNECT_DELAYS[Math.min(reconnectAttempt, RECONNECT_DELAYS.length - 1)];
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(connect, delay);
    };

    const connect = () => {
      if (!active || !navigator.onLine) {
        setState('closed');
        return;
      }
      if (socket && (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)) return;
      clearReconnectTimer();
      setState('connecting');
      socket = new WebSocket(websocketUrl(path));
      socket.addEventListener('open', () => {
        if (!active) return;
        reconnectAttempt = 0;
        setState('open');
        onOpenRef.current?.();
      });
      socket.addEventListener('message', (message) => {
        try {
          onEventRef.current(JSON.parse(String(message.data)) as T);
        } catch {
          // Ignore malformed events and keep the socket alive.
        }
      });
      socket.addEventListener('close', () => {
        if (!active) return;
        socket = null;
        setState('closed');
        scheduleReconnect();
      });
      socket.addEventListener('error', () => socket?.close());
    };

    const handleOffline = () => {
      clearReconnectTimer();
      setState('closed');
      socket?.close();
    };

    const handleOnline = () => {
      reconnectAttempt = 0;
      connect();
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    connect();
    return () => {
      active = false;
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      clearReconnectTimer();
      const currentSocket = socket;
      if (currentSocket) {
        if (currentSocket.readyState === WebSocket.CONNECTING) {
          currentSocket.onopen = () => { currentSocket.close(); };
        } else if (currentSocket.readyState === WebSocket.OPEN) {
          currentSocket.close();
        }
      }
    };
  }, [path]);

  return state;
}
